import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth";
import { toast } from "sonner";

// Unlock methods supported: password, 6-digit PIN (if set), passkey. Any
// one passes. The PIN field is hidden when the user hasn't set one, so
// the UI never looks broken for accounts that opted out.
const UnlockSchema = z.object({
  password: z.string().optional(),
  pin: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{6}$/.test(v), { message: "6 digits" }),
});

export function LockOverlay({ onUnlock }: { onUnlock: () => void }) {
  const pinStatus = useQuery<{ hasLockPin: boolean }>({
    queryKey: ["/api/me/lock-pin"],
    queryFn: async () => {
      const r = await fetch("/api/me/lock-pin", { credentials: "include" });
      if (!r.ok) return { hasLockPin: false };
      return r.json();
    },
    // Cache hard — once we know whether the user has a PIN, we don't
    // need to ask again for the duration of the session. Avoids the
    // brief flash where the PIN field is hidden during the first
    // request after a lock event.
    staleTime: 5 * 60 * 1000,
  });
  // Show the PIN field optimistically until we hear definitively that
  // the user has no PIN. Hiding too eagerly was causing the input to
  // be missing right after the modal opened (query in flight) and
  // permanently when the modal flashed over an unauthenticated session
  // (the request 401'd and cached `false`).
  const hasPin = pinStatus.isSuccess ? pinStatus.data.hasLockPin : true;

  const { register, handleSubmit, setValue, watch } = useForm<z.infer<typeof UnlockSchema>>({
    resolver: zodResolver(UnlockSchema),
  });
  const pinValue = watch("pin") ?? "";
  const [busy, setBusy] = useState(false);

  async function onSubmit(d: z.infer<typeof UnlockSchema>) {
    if (!d.password && !d.pin) {
      toast.error("Enter password or PIN");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/session/unlock", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(d.password ? { password: d.password } : {}),
          ...(d.pin ? { pin: d.pin } : {}),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        toast.error(body.message ?? "Unlock failed");
        return;
      }
      onUnlock();
    } finally {
      setBusy(false);
    }
  }

  async function tryPasskey() {
    // Route through Better Auth's signIn.passkey — performs the full
    // WebAuthn assertion and creates a fresh authenticated session
    // (replacing the locked one). Behaviorally identical to "unlock"
    // for the user, but cryptographically grounded: the assertion is
    // verified server-side before any session change happens.
    //
    // Replaces the previous flow which called a custom
    // /api/session/unlock-passkey that didn't actually verify the
    // assertion — anyone with the locked session cookie could unlock.
    try {
      const res = await authClient.signIn.passkey();
      if (res?.error) throw new Error(res.error.message ?? "Passkey failed");
      onUnlock();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-background">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-lg">
        <h2 className="font-display text-xl font-bold">Locked</h2>
        <p className="mb-4 text-xs text-muted-foreground">Unlock to continue.</p>
        <Button variant="outline" className="mb-3 w-full" onClick={tryPasskey}>
          Use passkey
        </Button>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {hasPin && (
            <div className="space-y-1">
              <Label htmlFor="pin">PIN</Label>
              <Input
                id="pin"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                autoComplete="off"
                placeholder="••••••"
                value={pinValue}
                onChange={(e) => {
                  // Strip non-digits as you type, cap at 6 chars, and
                  // auto-submit the moment all 6 are present so the
                  // user doesn't have to reach for the Unlock button.
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setValue("pin", digits, { shouldValidate: true });
                  if (digits.length === 6 && !busy) {
                    void handleSubmit(onSubmit)();
                  }
                }}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...register("password")}
            />
          </div>
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Unlocking…" : "Unlock"}
          </Button>
        </form>
      </div>
    </div>
  );
}
